import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, MessageSquare, Settings, Shield, Eye,
    Check, ChevronRight, ChevronLeft, Calendar, Layers,
    Plus, Trash2, GripVertical, AlertCircle
} from 'lucide-react';
import QuestionList from '../Surveys/QuestionList';
import AssignmentPlaceholderPicker from './AssignmentPlaceholderPicker';
import PlaceholderTextarea from './PlaceholderTextarea';

// ═══════════════════════════════════════════════════════════════════════════════
// SurveyDetailEditor — inline survey detail page within AICourseGenerator
// ═══════════════════════════════════════════════════════════════════════════════
const SurveyDetailEditor = ({ survey, timelineItem, onSave, onBack }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        is_anonymous: false,
        is_compliance_required: false,
        questions: [],
        assignment_placeholders: []
    });

    const [activeStep, setActiveStep] = useState('content');
    const [hasChanges, setHasChanges] = useState(false);
    const [isAnimatingIn, setIsAnimatingIn] = useState(true);

    const steps = ['content', 'settings'];
    const currentStepIndex = steps.indexOf(activeStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === steps.length - 1;

    // ── Initialize ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (survey) {
            setFormData({
                title: survey.title || '',
                description: survey.description || '',
                is_anonymous: survey.is_anonymous || false,
                is_compliance_required: survey.is_compliance_required || false,
                questions: survey.questions || [],
                assignment_placeholders: timelineItem?.assignment_placeholders || survey.assignment_placeholders || []
            });
            setHasChanges(false);
        }
    }, [survey, timelineItem]);

    useEffect(() => {
        const timer = setTimeout(() => setIsAnimatingIn(false), 300);
        return () => clearTimeout(timer);
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setHasChanges(true);
    }, []);

    const handleQuestionsChange = useCallback((questions) => {
        setFormData(prev => ({ ...prev, questions }));
        setHasChanges(true);
    }, []);

    const handleApplyAndBack = useCallback(() => {
        onSave(formData);
        onBack();
    }, [formData, onSave, onBack]);

    const handleBackWithoutSaving = useCallback(() => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Discard them?')) {
                onBack();
            }
        } else {
            onBack();
        }
    }, [hasChanges, onBack]);

    const triggerDay = timelineItem?.trigger_day ?? timelineItem?.relative_day ?? '?';

    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div className={`flex flex-col h-full bg-white dark:bg-slate-900 transition-all duration-300 ${isAnimatingIn ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>

            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
                    <button
                        onClick={handleBackWithoutSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                        Timeline
                    </button>

                    <div className="w-px h-6 bg-gray-200 dark:bg-slate-700" />

                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                            <MessageSquare className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-semibold text-gray-800 dark:text-slate-100 truncate text-sm sm:text-base">
                                {formData.title || 'Untitled Survey'}
                            </h2>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                                    Survey
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Day {triggerDay}
                                </span>
                                {formData.questions.length > 0 && (
                                    <>
                                        <span>•</span>
                                        <span>{formData.questions.length} questions</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleApplyAndBack}
                        disabled={!formData.title?.trim()}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${hasChanges
                            ? 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white shadow-lg shadow-indigo-500/25'
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {hasChanges ? (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">Apply Changes</span>
                                <span className="sm:hidden">Apply</span>
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                <span className="hidden sm:inline">No Changes</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center gap-2 px-4 sm:px-6 pb-3">
                    {[
                        { id: 'content', label: 'Questions', icon: Layers, step: 1 },
                        { id: 'settings', label: 'Settings', icon: Settings, step: 2 }
                    ].map(({ id, label, icon: Icon, step }, index) => {
                        const stepIdx = steps.indexOf(id);
                        const isCompleted = stepIdx < currentStepIndex;
                        const isCurrent = id === activeStep;

                        return (
                            <React.Fragment key={id}>
                                <button
                                    onClick={() => setActiveStep(id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isCurrent
                                        ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-500/30'
                                        : isCompleted
                                            ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10'
                                            : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isCompleted
                                        ? 'bg-green-500 text-white'
                                        : isCurrent
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                        }`}>
                                        {isCompleted ? <Check className="w-3 h-3" /> : step}
                                    </div>
                                    <span className="hidden sm:inline">{label}</span>
                                </button>
                                {index < steps.length - 1 && (
                                    <div className={`w-6 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-700'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── Content Area ───────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                {activeStep === 'content' && (
                    <div className="space-y-5 p-4 sm:p-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter survey title"
                                className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent text-lg"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Description
                            </label>
                            <PlaceholderTextarea
                                value={formData.description}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, description: e.target.value }));
                                    setHasChanges(true);
                                }}
                                placeholder="Brief description of this survey — use {employee.name} etc. for personalization"
                                rows={3}
                            />
                        </div>

                        {/* Questions */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                                Questions
                            </label>
                            <QuestionList
                                questions={formData.questions}
                                setQuestions={handleQuestionsChange}
                            />
                        </div>

                        {/* Assignment Placeholders */}
                        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                            <AssignmentPlaceholderPicker
                                value={formData.assignment_placeholders || []}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, assignment_placeholders: val }));
                                    setHasChanges(true);
                                }}
                            />
                        </div>
                    </div>
                )}

                {activeStep === 'settings' && (
                    <div className="space-y-5 p-4 sm:p-6">
                        {/* Anonymous Toggle */}
                        <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                            <input
                                type="checkbox"
                                name="is_anonymous"
                                checked={formData.is_anonymous}
                                onChange={handleChange}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <div>
                                <div className="flex items-center gap-2">
                                    <Eye className="w-4 h-4 text-indigo-500" />
                                    <span className="text-sm font-medium text-gray-800 dark:text-slate-100">Anonymous Responses</span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Employee responses will be kept anonymous</p>
                            </div>
                        </label>

                        {/* Compliance Toggle */}
                        <div className={`rounded-xl p-4 border-2 transition-all ${formData.is_compliance_required
                            ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-500/10 dark:to-orange-500/10 border-red-200 dark:border-red-500/30'
                            : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700'
                            }`}>
                            <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${formData.is_compliance_required
                                    ? 'bg-red-100 dark:bg-red-500/20'
                                    : 'bg-gray-200 dark:bg-slate-700'
                                    }`}>
                                    <Shield className={`w-5 h-5 ${formData.is_compliance_required
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-500 dark:text-slate-400'
                                        }`} />
                                </div>
                                <div className="flex-1">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="is_compliance_required"
                                            checked={formData.is_compliance_required}
                                            onChange={handleChange}
                                            className={`w-4 h-4 rounded focus:ring-2 ${formData.is_compliance_required
                                                ? 'text-red-600 focus:ring-red-500'
                                                : 'text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                        />
                                        <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                                            Compliance Required
                                        </span>
                                    </label>
                                    <p className="text-xs text-gray-600 dark:text-slate-300 mt-1 ml-6">
                                        This survey is mandatory. Incomplete responses will be flagged.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                        {hasChanges ? (
                            <>
                                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                <span className="text-xs">Unsaved changes</span>
                            </>
                        ) : (
                            <>
                                <div className="w-2 h-2 bg-green-500 rounded-full" />
                                <span className="text-xs">Up to date</span>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {!isFirstStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex - 1])}
                                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}
                        {!isLastStep && (
                            <button
                                onClick={() => setActiveStep(steps[currentStepIndex + 1])}
                                className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                        {isLastStep && (
                            <button
                                onClick={handleApplyAndBack}
                                disabled={!formData.title?.trim()}
                                className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-lg transition-all shadow-lg shadow-indigo-500/25 text-sm font-semibold disabled:opacity-50"
                            >
                                <Check className="w-4 h-4" />
                                Apply & Return
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyDetailEditor;
